import type { CampaignProduct } from '@bvengineering/cas-member-gateway-service-client';
import { Button, Modal, RadioSelect, Typography } from '@/primer/components';
import { variantInStock } from '@/utils';

interface Props {
  product: CampaignProduct | null;
  isReserved: boolean;
  draftVariantGtin: string | undefined;
  onVariantChange: (gtin: string | undefined) => void;
  selectDisabled: boolean;
  isUnreserving: boolean;
  onClose: () => void;
  onSelect: () => void;
  onUnselect: () => void;
}

/** Product detail sheet shown when a creator taps a product card on SelectProduct. */
export function ProductDetailsSheet({
  product,
  isReserved,
  draftVariantGtin,
  onVariantChange,
  selectDisabled,
  isUnreserving,
  onClose,
  onSelect,
  onUnselect,
}: Props) {
  const hasVariants = (product?.variants?.length ?? 0) !== 0;

  return (
    <Modal isOpen={product != null} onClose={onClose}>
      {product && (
        <div className="flex flex-col gap-4">
          <div className="w-full aspect-square flex items-center justify-center">
            {product.productImageUrl ? (
              <img
                src={product.productImageUrl}
                alt={product.productName ?? 'Product'}
                className="max-w-full max-h-full object-contain"
              />
            ) : (
              <div className="w-full h-full bg-disabled rounded-medium" aria-hidden="true" />
            )}
          </div>

          <div className="flex flex-col gap-1">
            {product.productBrand && (
              <Typography variant="body" size="small" color="bodylight">
                {product.productBrand}
              </Typography>
            )}
            <Typography variant="header" size="h3" as="h3">
              {product.productName ?? 'Product'}
            </Typography>
          </div>

          {product.productDescription && (
            <Typography variant="body" size="medium">
              {product.productDescription}
            </Typography>
          )}

          {hasVariants && (
            <RadioSelect
              label="Choose an option"
              options={product.variants!.map((variant) => ({
                value: variant.gtin,
                label: variant.name ?? variant.gtin,
                disabled: !variantInStock(variant),
              }))}
              value={draftVariantGtin}
              onChange={onVariantChange}
            />
          )}

          <div className="flex flex-col gap-4 pt-2">
            {isReserved ? (
              <Button variant="primary" fullWidth isLoading={isUnreserving} onClick={onUnselect}>
                Unselect
              </Button>
            ) : (
              <Button variant="primary" fullWidth onClick={onSelect} disabled={selectDisabled}>
                Select
              </Button>
            )}
            <Button variant="secondary" fullWidth onClick={onClose}>
              Back
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default ProductDetailsSheet;
